import React, { useState } from 'react';
import { Mail, MessageSquare, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/buttons/BackButton';
import HeaderNoAuth from '../components/Header/HeaderNoAuth';
import Card from '../components/Card/Card';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import Button from '../components/buttons/Button';
import { ContactService } from '../services/ContactService';
import './Contact.css';

const contactService = new ContactService();

const Contact = () => {
  const { isSubscribed, profile } = useAuth();
  const isAuthenticated = isSubscribed || profile;
  const [formData, setFormData] = useState({
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear submit status when user starts typing again
    if (submitStatus) {
      setSubmitStatus(null);
      setErrorMessage('');
    }
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      setErrorMessage('Veuillez entrer votre adresse email');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Veuillez entrer une adresse email valide');
      return false;
    }

    if (!formData.subject.trim()) {
      setErrorMessage('Veuillez entrer un sujet');
      return false;
    }

    if (!formData.message.trim()) {
      setErrorMessage('Veuillez entrer votre message');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await contactService.sendContactMessage(
        formData.email,
        formData.subject,
        formData.message
      );

      if (response.success) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
        setErrorMessage(response.message || 'Une erreur est survenue lors de l\'envoi du message');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(
        error.message ||
        'Impossible d\'envoyer le message. Veuillez réessayer plus tard.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {!isAuthenticated && <HeaderNoAuth />}
      {!isAuthenticated && <BackButton to="/landing" />}
      {isAuthenticated && <BackButton to="/guide" />}

      {/* Header Section */}
      <header className="contact-header">
        <div className="header-content">
          <div className="header-icon-wrapper">
            <Mail size={48} />
          </div>
          <h1>Contactez-nous</h1>
          <p className="contact-subtitle">
            Une question, une suggestion ou besoin d'aide ? Nous sommes là pour vous écouter.
          </p>
        </div>
      </header>

      <div className="settings-container">

        {/* Contact Form Card */}
        <Card className="card-large">
          <SectionHeader icon={MessageSquare} title="Envoyez-nous un message" />

          <form className="contact-form" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email">
                Adresse email <span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre.email@exemple.ch"
                  disabled={isSubmitting}
                  required
                  aria-required="true"
                />
              </div>
            </div>

            {/* Subject Field */}
            <div className="form-group">
              <label htmlFor="subject">
                Sujet <span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Ex: Question sur les fonctionnalités"
                  disabled={isSubmitting}
                  required
                  aria-required="true"
                />
              </div>
            </div>

            {/* Message Field */}
            <div className="form-group">
              <label htmlFor="message">
                Message <span className="required">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Décrivez votre question ou suggestion en détail..."
                rows="6"
                disabled={isSubmitting}
                required
                aria-required="true"
              />
            </div>

            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="status-message success-message">
                <CheckCircle size={20} />
                <span>Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.</span>
              </div>
            )}

            {/* Error Message */}
            {submitStatus === 'error' && (
              <div className="status-message error-message">
                <AlertCircle size={20} />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="form-actions">
              <Button
                variant="primary"
                size="large"
                icon={Send}
                htmlType="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
              </Button>
            </div>

            <p className="form-note">
              <span className="required">*</span> Champs obligatoires
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Contact;
